import React from 'react';
import { Link } from 'react-router-dom';

const BuildingOwnersLanding: React.FC = () => {
    return (
        <div className="min-h-screen bg-gray-50">
            {/* Hero Section */}
            <div className="bg-gradient-to-r from-green-600 to-blue-700 text-white py-20">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-6xl font-bold mb-6">
                            Building Owners & Property Managers
                        </h1>
                        <p className="text-xl md:text-2xl text-green-100 max-w-4xl mx-auto mb-8">
                            Protect your investment with Local Law 97 compliance strategies and cost-effective retrofit solutions
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <Link 
                                to="/building-analysis" 
                                className="bg-white text-green-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                            >
                                🏢 Analyze My Building
                            </Link>
                            <Link 
                                to="/ll97-calculator" 
                                className="bg-green-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-green-400 border-2 border-white transition-colors"
                            >
                                💰 Calculate Potential Fines
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Key Benefits Section */}
            <div className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Why Building Owners Choose Our Platform
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Navigate Local Law 97 with confidence and maximize your property's value
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="text-2xl mb-4">💰</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Avoid Costly Fines</h3>
                            <p className="text-gray-600">
                                Calculate potential LL97 fines starting in 2024 and plan ahead to minimize financial impact on your property investment.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl mb-4">📈</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Increase Property Value</h3>
                            <p className="text-gray-600">
                                Energy-efficient buildings command higher rents, have lower vacancy rates, and attract quality tenants.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="text-2xl mb-4">🎯</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Targeted Solutions</h3>
                            <p className="text-gray-600">
                                Get customized retrofit recommendations based on your building's specific energy profile and compliance needs.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Problem & Solution Section */}
            <div className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                The Challenge You're Facing
                            </h2>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <span className="text-red-500 text-xl">⚠️</span>
                                    <p className="text-gray-600">
                                        <strong>LL97 fines start in 2024</strong> - Buildings exceeding carbon limits face substantial penalties
                                    </p>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <span className="text-red-500 text-xl">📊</span>
                                    <p className="text-gray-600">
                                        <strong>Complex compliance requirements</strong> - Understanding energy limits and reporting obligations is challenging
                                    </p>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <span className="text-red-500 text-xl">💸</span>
                                    <p className="text-gray-600">
                                        <strong>Retrofit decision paralysis</strong> - Too many options, unclear ROI, and uncertain contractor reliability
                                    </p>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <span className="text-red-500 text-xl">⏰</span>
                                    <p className="text-gray-600">
                                        <strong>Time is running out</strong> - The clock is ticking to implement energy-saving measures
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div>
                            <h2 className="text-3xl font-bold text-gray-900 mb-6">
                                Our Solution for You
                            </h2>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <span className="text-green-500 text-xl">✅</span>
                                    <p className="text-gray-600">
                                        <strong>Instant compliance assessment</strong> - Know your building's status and fine exposure immediately
                                    </p>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <span className="text-green-500 text-xl">🎯</span>
                                    <p className="text-gray-600">
                                        <strong>Prioritized retrofit recommendations</strong> - Focus on the most cost-effective improvements first
                                    </p>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <span className="text-green-500 text-xl">🤝</span>
                                    <p className="text-gray-600">
                                        <strong>Vetted contractor network</strong> - Connect with experienced, reliable professionals in your area
                                    </p>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <span className="text-green-500 text-xl">💡</span>
                                    <p className="text-gray-600">
                                        <strong>Ongoing support</strong> - Get expert guidance throughout your compliance journey
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tools & Services Section */}
            <div className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Essential Tools for Building Owners
                        </h2>
                        <p className="text-xl text-gray-600">
                            Everything you need to ensure compliance and maximize your property investment
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {/* Building Analysis */}
                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🏢</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Building Analysis</h3>
                            <p className="text-gray-600 mb-4">
                                Comprehensive analysis of your property's energy performance and compliance status.
                            </p>
                            <Link 
                                to="/building-analysis" 
                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Analyze Building →
                            </Link>
                        </div>

                        {/* LL97 Calculator */}
                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">💰</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">LL97 Fine Calculator</h3>
                            <p className="text-gray-600 mb-4">
                                Calculate potential fines and understand your financial exposure under Local Law 97.
                            </p>
                            <Link 
                                to="/ll97-calculator" 
                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Calculate Fines →
                            </Link>
                        </div>

                        {/* Retrofit Calculator */}
                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🔧</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Retrofit Calculator</h3>
                            <p className="text-gray-600 mb-4">
                                Evaluate retrofit options, costs, and payback periods for your specific building.
                            </p>
                            <Link 
                                to="/retrofit-calculator" 
                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Explore Retrofits →
                            </Link>
                        </div>

                        {/* Simple Building View */}
                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">📋</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Quick Building Check</h3>
                            <p className="text-gray-600 mb-4">
                                Fast, simple overview of your building's energy performance and compliance status.
                            </p>
                            <Link 
                                to="/simple-view" 
                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Quick Check →
                            </Link>
                        </div>

                        {/* Contractor Network */}
                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🔨</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">Contractor Network</h3>
                            <p className="text-gray-600 mb-4">
                                Connect with pre-vetted contractors experienced in LL97 compliance retrofits.
                            </p>
                            <Link 
                                to="/contractor-partnership-signup" 
                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                                Find Contractors →
                            </Link>
                        </div>

                        {/* Chinese Services */}
                        <div className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-shadow">
                            <div className="text-2xl mb-4">🇨🇳</div>
                            <h3 className="text-xl font-semibold text-gray-900 mb-3">中文服务 Chinese Services</h3>
                            <p className="text-gray-600 mb-4">
                                Dedicated Chinese-language support for building owners and property managers.
                            </p>
                            <Link 
                                to="/chinese-building-owners" 
                                className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium"
                            >
                                中文服务 →
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Success Stories */}
            <div className="py-16 bg-gray-50">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                            Success Stories from Building Owners
                        </h2>
                        <p className="text-xl text-gray-600">
                            Real results from property owners who took action
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-xl mb-4">📈</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Midtown Office Building</h3>
                            <p className="text-gray-600 mb-4">
                                "Avoided $45,000 in annual LL97 fines by implementing targeted HVAC upgrades identified through the platform."
                            </p>
                            <div className="text-sm text-gray-500">
                                — Property Manager, 150-unit office building
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-xl mb-4">💡</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Brooklyn Residential Complex</h3>
                            <p className="text-gray-600 mb-4">
                                "The retrofit calculator helped us prioritize improvements that paid for themselves in 3 years while avoiding fines."
                            </p>
                            <div className="text-sm text-gray-500">
                                — Building Owner, 75-unit residential
                            </div>
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-6">
                            <div className="text-xl mb-4">🤝</div>
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Manhattan Mixed-Use</h3>
                            <p className="text-gray-600 mb-4">
                                "Connected with reliable contractors through the platform. Project completed on time and under budget."
                            </p>
                            <div className="text-sm text-gray-500">
                                — Property Owner, 12-story mixed-use
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="py-16 bg-gradient-to-r from-blue-600 to-purple-700 text-white">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl md:text-4xl font-bold mb-6">
                        Take Action on LL97 Compliance Today
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                        Don't wait until 2024 when fines begin. Start your compliance journey now and protect your investment.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <Link 
                            to="/building-analysis" 
                            className="bg-white text-blue-700 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-gray-100 transition-colors"
                        >
                            🏢 Start Building Analysis
                        </Link>
                        <Link 
                            to="/contact" 
                            className="bg-blue-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-blue-400 border-2 border-white transition-colors"
                        >
                            📞 Schedule Consultation
                        </Link>
                    </div>
                    <p className="text-sm text-blue-200 mt-6">
                        Free initial analysis • No commitment required • Expert support available
                    </p>
                </div>
            </div>
        </div>
    );
};

export default BuildingOwnersLanding;
